/**
 * Runner Detail Page.
 *
 * Displays full runner details: system info, certificate status,
 * capabilities, and provides revoke action.
 *
 * Sprint: 40, Task: S40-C04
 */

import { useParams, useNavigate, Link } from 'react-router-dom';
import { useI18n } from '@/i18n';
import {
  Button,
  Card,
  Spinner,
  StatusBadge,
} from '@/components/ui';
import { useRunner, useDeleteRunner, useGrantRunnerAccess, useRevokeRunnerAccess, useChangeRunnerMode, useRenewCertificate } from '@/features/mcp';
import type { RunnerStatus, CertificateStatus } from '@/features/mcp';
import { useTenantMembers } from '@/features/permissions/hooks';
import { useModuleAccess } from '@/hooks/useModuleAccess';
import {
  ArrowLeft,
  Cpu,
  Monitor,
  Shield,
  AlertTriangle,
  Trash2,
  Users,
  Plus,
  X,
  ToggleLeft,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { useState } from 'react';

// =============================================================================
// Helpers
// =============================================================================

function runnerStatusType(status: RunnerStatus) {
  switch (status) {
    case 'online': return 'healthy' as const;
    case 'offline': return 'default' as const;
    case 'error': return 'error' as const;
    case 'pending': return 'warning' as const;
    default: return 'default' as const;
  }
}

function certStatusType(status: CertificateStatus) {
  switch (status) {
    case 'active': return 'healthy' as const;
    case 'pending': return 'warning' as const;
    case 'expired': return 'error' as const;
    case 'revoked': return 'error' as const;
    default: return 'default' as const;
  }
}

function osLabel(os: string): string {
  switch (os.toLowerCase()) {
    case 'darwin': return 'macOS';
    case 'linux': return 'Linux';
    case 'windows': return 'Windows';
    default: return os || '—';
  }
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString();
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-2 text-sm border-b last:border-b-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}

// =============================================================================
// Component
// =============================================================================

export function RunnerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();
  const canManageRunnerPolicy = useModuleAccess('runner', 'admin');

  const { data: runner, isLoading, error } = useRunner(id!);
  const deleteMutation = useDeleteRunner();
  const grantAccessMutation = useGrantRunnerAccess();
  const revokeAccessMutation = useRevokeRunnerAccess();
  const modeMutation = useChangeRunnerMode();
  const renewMutation = useRenewCertificate();
  const { data: membersData } = useTenantMembers();

  const [currentMode, setCurrentMode] = useState<'normal' | 'validate_all'>('normal');
  const [accessType, setAccessType] = useState<'user' | 'group'>('user');
  const [accessId, setAccessId] = useState('');
  const [revokeType, setRevokeType] = useState<'user' | 'group'>('user');
  const [revokeId, setRevokeId] = useState('');

  const handleGrantAccess = async () => {
    if (!id || !accessId.trim()) return;
    try {
      await grantAccessMutation.mutateAsync({
        runnerId: id,
        userId: accessType === 'user' ? accessId.trim() : undefined,
        groupId: accessType === 'group' ? accessId.trim() : undefined,
      });
      toast.success(t('admin.runners.access.grantSuccess'));
      setAccessId('');
    } catch (err) {
      toast.error((err as Error).message || t('admin.runners.access.grantError'));
    }
  };

  const handleRevokeAccess = async () => {
    if (!id || !revokeId.trim()) return;
    try {
      await revokeAccessMutation.mutateAsync({
        runnerId: id,
        userId: revokeType === 'user' ? revokeId.trim() : undefined,
        groupId: revokeType === 'group' ? revokeId.trim() : undefined,
      });
      toast.success(t('admin.runners.access.revokeSuccess'));
      setRevokeId('');
    } catch (err) {
      toast.error((err as Error).message || t('admin.runners.access.revokeError'));
    }
  };

  const handleModeChange = async (mode: 'normal' | 'validate_all') => {
    if (!id) return;
    try {
      await modeMutation.mutateAsync({ runnerId: id, mode });
      setCurrentMode(mode);
      toast.success(t('admin.runners.mode.success'));
    } catch (err) {
      toast.error((err as Error).message || t('admin.runners.mode.error'));
    }
  };

  const handleRevoke = async () => {
    if (!id) return;
    if (!window.confirm(t('admin.runners.detail.revokeWarning'))) return;

    try {
      await deleteMutation.mutateAsync(id);
      toast.success('Runner revoked');
      navigate('/integrations/runners');
    } catch (err) {
      toast.error((err as Error).message || 'Failed to revoke runner');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner />
      </div>
    );
  }

  if (error || !runner) {
    return (
      <Card className="p-8 text-center">
        <p className="text-destructive">Runner not found</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/integrations/runners')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Runners
        </Button>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/integrations/runners"
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <Cpu className="w-6 h-6 text-primary" />
              <h1 className="text-2xl font-bold">{runner.name}</h1>
              <StatusBadge
                status={runnerStatusType(runner.status as RunnerStatus)}
                label={t(`admin.runners.status.${runner.status}`)}
              />
            </div>
            {runner.hostname && (
              <p className="text-muted-foreground mt-1 flex items-center gap-1">
                <Monitor className="w-4 h-4" />
                {runner.hostname}
              </p>
            )}
          </div>
        </div>
        {canManageRunnerPolicy && (
          <Link to={`/integrations/runners/${id}/policy`}>
            <Button variant="outline">
              <Shield className="w-4 h-4 mr-2" />
              {t('admin.integrations.runnerPolicy') ?? 'Política del runner'}
            </Button>
          </Link>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* System Info */}
        <Card className="p-5">
          <h3 className="flex items-center gap-2 font-semibold text-sm mb-4">
            <Monitor className="w-4 h-4 text-primary" />
            {t('admin.runners.detail.systemInfo')}
          </h3>
          <div>
            <InfoRow label={t('admin.runners.detail.hostname')} value={runner.hostname || '—'} />
            <InfoRow label={t('admin.runners.os')} value={osLabel(runner.os)} />
            <InfoRow label={t('admin.runners.arch')} value={runner.arch || '—'} />
            <InfoRow label={t('admin.runners.detail.version')} value={runner.agent_version || '—'} />
            <InfoRow label={t('admin.runners.detail.enrolledAt')} value={formatDate(runner.enrolled_at)} />
            <InfoRow label={t('admin.runners.lastSeen')} value={formatDate(runner.last_seen)} />
          </div>
        </Card>

        {/* Certificate Info */}
        <Card className="p-5">
          <h3 className="flex items-center gap-2 font-semibold text-sm mb-4">
            <Shield className="w-4 h-4 text-primary" />
            {t('admin.runners.certificate')}
          </h3>
          <div>
            <InfoRow
              label={t('common.status')}
              value={
                <StatusBadge
                  status={certStatusType(runner.certificate_status as CertificateStatus)}
                  label={t(`admin.runners.certStatus.${runner.certificate_status}`)}
                />
              }
            />
            <InfoRow
              label="Expires"
              value={formatDate(runner.certificate_expires_at)}
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            className="mt-3 w-full"
            disabled={renewMutation.isPending || runner.status === 'pending'}
            onClick={() => {
              renewMutation.mutate(runner.agent_id, {
                onSuccess: () => toast.success(t('admin.runners.renewSuccess')),
                onError: (err) => toast.error((err as Error).message || t('admin.runners.renewError')),
              });
            }}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${renewMutation.isPending ? 'animate-spin' : ''}`} />
            {t('admin.runners.renewCertificate')}
          </Button>
        </Card>

        {/* Capabilities */}
        <Card className="p-5">
          <h3 className="flex items-center gap-2 font-semibold text-sm mb-4">
            <Cpu className="w-4 h-4 text-primary" />
            {t('admin.runners.detail.capabilities')}
          </h3>
          {runner.capabilities.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {runner.capabilities.map((cap) => (
                <span
                  key={cap}
                  className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-medium"
                >
                  {cap}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">No capabilities reported</p>
          )}
        </Card>

        {/* Mode Control */}
        <Card className="p-5">
          <h3 className="flex items-center gap-2 font-semibold text-sm mb-4">
            <ToggleLeft className="w-4 h-4 text-primary" />
            {t('admin.runners.mode.title')}
          </h3>
          <p className="text-xs text-muted-foreground mb-4">
            {t('admin.runners.mode.description')}
          </p>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => handleModeChange('normal')}
              disabled={modeMutation.isPending}
              className={`text-sm px-3 py-1.5 rounded-md font-medium transition-colors ${
                currentMode === 'normal'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted hover:bg-muted/80'
              }`}
            >
              {t('admin.runners.mode.normal')}
            </button>
            <button
              type="button"
              onClick={() => handleModeChange('validate_all')}
              disabled={modeMutation.isPending}
              className={`text-sm px-3 py-1.5 rounded-md font-medium transition-colors ${
                currentMode === 'validate_all'
                  ? 'bg-amber-600 text-white'
                  : 'bg-muted hover:bg-muted/80'
              }`}
            >
              {t('admin.runners.mode.validateAll')}
            </button>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            {currentMode === 'validate_all'
              ? t('admin.runners.mode.validateAllHint')
              : t('admin.runners.mode.normalHint')}
          </p>
        </Card>

        {/* Access Management */}
        <Card className="p-5 lg:col-span-2">
          <h3 className="flex items-center gap-2 font-semibold text-sm mb-4">
            <Users className="w-4 h-4 text-primary" />
            {t('admin.runners.access.title')}
          </h3>
          <p className="text-xs text-muted-foreground mb-4">
            {t('admin.runners.access.description')}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Grant Access */}
            <div className="space-y-3 p-3 rounded-md border">
              <h4 className="text-sm font-medium flex items-center gap-1">
                <Plus className="w-3 h-3" />
                {t('admin.runners.access.grant')}
              </h4>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setAccessType('user')}
                  className={`text-xs px-2 py-1 rounded ${accessType === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}
                >
                  {t('admin.runners.access.typeUser')}
                </button>
                <button
                  type="button"
                  onClick={() => setAccessType('group')}
                  className={`text-xs px-2 py-1 rounded ${accessType === 'group' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}
                >
                  {t('admin.runners.access.typeGroup')}
                </button>
              </div>
              {accessType === 'user' ? (
                <select
                  value={accessId}
                  onChange={(e) => setAccessId(e.target.value)}
                  className="w-full text-sm px-3 py-2 border rounded-md bg-background"
                >
                  <option value="">{t('admin.runners.access.selectUser')}</option>
                  {(membersData?.items || []).map((m) => (
                    <option key={m.user_id} value={m.user_id}>
                      {m.name || m.email || m.user_id}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={accessId}
                  onChange={(e) => setAccessId(e.target.value)}
                  placeholder={t('admin.runners.access.groupIdPlaceholder')}
                  className="w-full text-sm px-3 py-2 border rounded-md bg-background"
                />
              )}
              <Button
                size="sm"
                onClick={handleGrantAccess}
                disabled={!accessId.trim() || grantAccessMutation.isPending}
              >
                <Plus className="w-3 h-3 mr-1" />
                {t('admin.runners.access.grant')}
              </Button>
            </div>

            {/* Revoke Access */}
            <div className="space-y-3 p-3 rounded-md border">
              <h4 className="text-sm font-medium flex items-center gap-1">
                <X className="w-3 h-3" />
                {t('admin.runners.access.revoke')}
              </h4>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setRevokeType('user')}
                  className={`text-xs px-2 py-1 rounded ${revokeType === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}
                >
                  {t('admin.runners.access.typeUser')}
                </button>
                <button
                  type="button"
                  onClick={() => setRevokeType('group')}
                  className={`text-xs px-2 py-1 rounded ${revokeType === 'group' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}
                >
                  {t('admin.runners.access.typeGroup')}
                </button>
              </div>
              {revokeType === 'user' ? (
                <select
                  value={revokeId}
                  onChange={(e) => setRevokeId(e.target.value)}
                  className="w-full text-sm px-3 py-2 border rounded-md bg-background"
                >
                  <option value="">{t('admin.runners.access.selectUser')}</option>
                  {(membersData?.items || []).map((m) => (
                    <option key={m.user_id} value={m.user_id}>
                      {m.name || m.email || m.user_id}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={revokeId}
                  onChange={(e) => setRevokeId(e.target.value)}
                  placeholder={t('admin.runners.access.groupIdPlaceholder')}
                  className="w-full text-sm px-3 py-2 border rounded-md bg-background"
                />
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={handleRevokeAccess}
                disabled={!revokeId.trim() || revokeAccessMutation.isPending}
              >
                <X className="w-3 h-3 mr-1" />
                {t('admin.runners.access.revoke')}
              </Button>
            </div>
          </div>
        </Card>

        {/* Danger Zone */}
        <Card className="p-5 border-destructive/30">
          <h3 className="flex items-center gap-2 font-semibold text-sm text-destructive mb-4">
            <AlertTriangle className="w-4 h-4" />
            {t('admin.integrations.dangerZone')}
          </h3>
          <p className="text-xs text-muted-foreground mb-4">
            {t('admin.runners.detail.revokeWarning')}
          </p>
          <Button
            variant="destructive"
            onClick={handleRevoke}
            disabled={deleteMutation.isPending}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            {t('admin.runners.detail.revoke')}
          </Button>
        </Card>
      </div>
    </div>
  );
}
